import { AxiosResponse } from 'axios'
import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import { CompareResponse } from '~/models/compare'
import { toCamelCase } from '~/utils/object'
import RequestBuilder from '~/builders/http/RequestBuilder'
import { CategoryId } from '~/models/category/types'

@containerScoped()
export default class CompareService {
  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  fetchComparableItems(
    ids: string[] = [],
    onlyDifferences: boolean | null
  ): Promise<CompareResponse> {
    return this.requestBuilder
      .request('get', '/api/classifieds/compare/')
      .params({ id: ids, only_differences: onlyDifferences })
      .map((body: AxiosResponse) => toCamelCase(this.normalizeData(body)))
      .send()
  }

  private normalizeData(body: AxiosResponse): CompareResponse {
    const { data } = body
    const { results } = data
    const numOfItems = results.num_of_items
    delete results.num_of_items
    const keys = Object.keys(results)
    const group: any = { itemSpecs: [] }
    const itemSpecs: Array<{ label: string; specs: Array<any> }> = []
    keys.forEach(k => {
      const { fields } = results[k]
      if (fields) {
        itemSpecs.push({ label: k, specs: this.groupFields(fields) })
      } else {
        group[k] = results[k]
      }
    })
    group.itemSpecs = itemSpecs

    return {
      results: group,
      comparableCategories: data.comparable_categories,
      numOfCategories: data.num_of_categories,
      rejected: data.rejected,
      category: data.category,
      numOfItems,
      classifieds: data.classifieds || [],
      categories: data.categories || [],
      page: data._page,
      categoryId: data.category_id
    }
  }

  addClassifiedToCompare(
    classifiedId: number | string
  ): Promise<{ compareIds: number[]; message: string }> {
    return this.requestBuilder
      .request('put', `/api/classifieds/compare/${classifiedId}/storage/`)
      .send()
  }

  removeClassifiedFromCompare(
    classifiedId: number | string
  ): Promise<{ compareIds: number[]; message: string }> {
    return this.requestBuilder
      .request('delete', `/api/classifieds/compare/${classifiedId}/storage/`)
      .send()
  }

  deleteComparedCategory(
    categoryId: number
  ): Promise<{
    compare: Record<CategoryId, number[]>
    message: string
  }> {
    return this.requestBuilder
      .request(
        'delete',
        `/api/classifieds/compare/category/${categoryId}/storage/`
      )
      .send()
  }

  private groupFields(fields: any): Array<Array<{ key: string; value: any }>> {
    const fieldsArray: any = []
    const fieldKeys = Object.keys(fields).sort()
    fieldKeys.forEach(k => {
      fieldsArray.push({ key: k, value: fields[k] })
    })
    return fieldsArray
  }
}
